import React from 'react';
import { Anchor, Box, DataTable, Page, PageContent, Text } from 'grommet';
import { API } from '@enrich/shared-lib';
import QueryString from 'qs';
import { useLoaderData } from 'react-router-dom';

const PAGE_SIZE = 100;

const contactName = (contactData) => {
    if (!contactData) {
        return null;
    }

    const { firstName, lastName } = contactData.attributes ?? {};

    return [firstName, lastName].filter(Boolean).join(' ') || null;
};

const referrerOf = (referral) => referral.attributes?.contact?.data ?? null;

const referredOf = (referral) =>
    referral.attributes?.referredContact?.data ?? null;

const referredLabel = (referral) =>
    contactName(referredOf(referral)) ||
    referral.attributes?.referred_name ||
    referral.attributes?.referred_email ||
    '';

export const ReferralsDashboard = () => {
    const { referrals, error } = useLoaderData() ?? {};

    const rows = React.useMemo(
        () =>
            (referrals ?? []).map((referral) => ({
                ...referral,
                referrerLabel: contactName(referrerOf(referral)) || '',
                referredLabel: referredLabel(referral),
            })),
        [referrals],
    );

    const dataColumns = [
        {
            header: 'Referred by',
            property: 'referrerLabel',
            render: (referral) => {
                const referrer = referrerOf(referral);

                return referrer ? (
                    <Anchor
                        href={`/contacts/${referrer.id}`}
                        label={referral.referrerLabel}
                    />
                ) : (
                    <Text>—</Text>
                );
            },
            sortable: true,
        },
        {
            header: 'Referred',
            property: 'referredLabel',
            render: (referral) => {
                const referred = referredOf(referral);

                if (referred) {
                    return (
                        <Anchor
                            href={`/contacts/${referred.id}`}
                            label={referral.referredLabel}
                        />
                    );
                }

                return <Text>{referral.referredLabel || '—'}</Text>;
            },
            sortable: true,
        },
        {
            header: 'Status',
            property: 'attributes.status',
            sortable: true,
        },
        {
            header: 'Referred on',
            property: 'attributes.createdAt',
            render: (referral) =>
                referral.attributes?.createdAt
                    ? new Date(
                          referral.attributes.createdAt,
                      ).toLocaleDateString()
                    : '—',
            sortable: true,
        },
    ];

    if (error) {
        return (
            <Page
                kind="full"
                pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
                <PageContent>
                    <Box pad="medium">
                        <Text>
                            Referrals could not be loaded. Please refresh to try
                            again.
                        </Text>
                    </Box>
                </PageContent>
            </Page>
        );
    }

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent>
                {rows.length === 0 ? (
                    <Box pad="medium">
                        <Text>No referrals yet.</Text>
                    </Box>
                ) : (
                    <DataTable
                        primaryKey="id"
                        sortable
                        paginate
                        step={25}
                        data={rows}
                        columns={dataColumns}
                    />
                )}
            </PageContent>
        </Page>
    );
};

const referralsQuery = (page) =>
    QueryString.stringify({
        populate: {
            contact: { fields: ['firstName', 'lastName', 'email'] },
            referredContact: { fields: ['firstName', 'lastName', 'email'] },
        },
        sort: ['createdAt:desc'],
        pagination: { page, pageSize: PAGE_SIZE },
    });

export async function loader() {
    const referrals = [];

    try {
        let page = 1;
        let pageCount = 1;

        do {
            const response = await API.get(
                `/api/referrals?${referralsQuery(page)}`,
            );

            referrals.push(...(response.data?.data ?? []));
            pageCount = response.data?.meta?.pagination?.pageCount ?? 1;
            page += 1;
        } while (page <= pageCount);
    } catch (error) {
        console.log(error);
        return { referrals: [], error: true };
    }

    return { referrals, error: false };
}
